import {
  type RepresentationData,
  componentTypeDefaultShape,
} from '@ardoq/data-model';
import { getIcons } from '@ardoq/icons';
import type { BlocksViewSettings } from './types';
import { GraphInterface, LabelsInterface } from '@ardoq/graph';

const DEFAULT_REPRESENTATION_DATA: RepresentationData = {
  isImage: false,
  value: null,
  shapeName: componentTypeDefaultShape,
};

const blocksViewRepresentationData = (
  viewInterface: LabelsInterface & GraphInterface,
  modelId: string,
  componentStyle: BlocksViewSettings['componentStyle']
): RepresentationData => {
  const componentImage = viewInterface.getComponentFieldValue(
    modelId,
    'image'
  ) as string | null;
  const componentIcon = viewInterface.getComponentFieldValue(
    modelId,
    'icon'
  ) as string | null;
  const componentShape = viewInterface.getComponentFieldValue(
    modelId,
    'shape'
  ) as string | null;

  const componentType = viewInterface.getComponentType(modelId);

  // If component has explicit image, always use it (regardless of componentStyle)
  if (componentImage) {
    return { isImage: true, value: componentImage };
  }

  const typeShape = componentType?.shape;
  const typeImage = componentType?.image;
  const typeIcon = componentType?.icon;
  const iconToUse = componentIcon || (typeImage ? null : typeIcon);
  const value = typeImage || iconToUse || null;

  if (componentStyle === 'shape') {
    const shapeToUse = componentShape || typeShape;

    return {
      isImage: false,
      value,
      icon: iconToUse ? getIcons()[iconToUse] : undefined,
      shapeName: shapeToUse || componentTypeDefaultShape,
    };
  }

  if (componentStyle === 'icon') {
    if (componentIcon) {
      return {
        isImage: false,
        value,
        icon: getIcons()[componentIcon],
      };
    }

    if (componentShape) {
      return { isImage: false, value, shapeName: componentShape };
    }

    if (typeIcon) {
      return { isImage: false, value, icon: getIcons()[typeIcon] };
    }

    const typeImage = componentType?.image;
    if (typeImage) {
      return { isImage: true, value: typeImage };
    }

    const typeShape = componentType?.shape;
    if (typeShape) {
      return { isImage: false, value: null, shapeName: typeShape };
    }

    return DEFAULT_REPRESENTATION_DATA;
  }

  if (!componentType) {
    if (componentShape) {
      return { isImage: false, value: null, shapeName: componentShape };
    }
    if (componentIcon) {
      return {
        isImage: false,
        value: componentIcon,
        icon: getIcons()[componentIcon],
      };
    }
    return DEFAULT_REPRESENTATION_DATA;
  }

  const { image, shape } = componentType;

  if (image) {
    return { isImage: true, value: image };
  }

  if (componentShape || shape) {
    const shapeToUse = componentShape || shape;
    return { isImage: false, value: null, shapeName: shapeToUse };
  }

  if (iconToUse) {
    return { isImage: false, value: iconToUse, icon: getIcons()[iconToUse] };
  }

  return DEFAULT_REPRESENTATION_DATA;
};
export { blocksViewRepresentationData };
